import React, { useEffect, useState } from "react";
import { Check, X, Trash2, RefreshCcw, Pin, PinOff, Heart, MessageCircle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

function StatusPill({ status }) {
  const s = status || "pending";
  const map = {
    pending: { bg: "bg-yellow-500/15", fg: "text-yellow-400", border: "border-yellow-500/30" },
    approved: { bg: "bg-[#CFF048]/15", fg: "text-[#CFF048]", border: "border-[#CFF048]/40" },
    rejected: { bg: "bg-red-500/15", fg: "text-red-400", border: "border-red-500/30" },
  };
  const c = map[s] || map.pending;
  return (
    <span className={`label-mono ${c.bg} ${c.fg} ${c.border} border px-2 py-0.5 text-[10px] uppercase`}>
      {s}
    </span>
  );
}

export default function AdminCommunity() {
  const [tab, setTab] = useState("pending");
  const [posts, setPosts] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [list, ct] = await Promise.all([
        api.get(`/admin/community/posts?status=${tab}`),
        api.get("/admin/community/counts"),
      ]);
      setPosts(list.data || []);
      setCounts(ct.data || { pending: 0, approved: 0, rejected: 0 });
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const act = async (id, action) => {
    setActingId(id);
    try {
      if (action === "delete") {
        await api.delete(`/admin/community/posts/${id}`);
        toast.success("POST DELETED");
      } else {
        await api.post(`/admin/community/posts/${id}/${action}`);
        const msgMap = {
          approve: "APPROVED — NOW LIVE ON THE WALL",
          reject: "REJECTED",
          pin: "PINNED TO TOP",
          unpin: "UNPINNED",
        };
        toast.success(msgMap[action] || "Done");
      }
      await load();
    } catch (err) {
      toast.error("Action failed", { description: err?.response?.data?.detail || "" });
    } finally {
      setActingId("");
    }
  };

  return (
    <AdminLayout title="Community · Moderation">
      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="community-stats">
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// PENDING</p>
          <p className="font-display font-black text-2xl text-yellow-400 mt-1">{counts.pending}</p>
        </div>
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// LIVE</p>
          <p className="font-display font-black text-2xl text-[#CFF048] mt-1">{counts.approved}</p>
        </div>
        <div className="border border-[#27272A] px-4 py-3">
          <p className="label-mono text-[#71717A]">// REJECTED</p>
          <p className="font-display font-black text-2xl text-red-400 mt-1">{counts.rejected}</p>
        </div>
        <Link
          to="/community"
          target="_blank"
          rel="noopener noreferrer"
          className="border border-[#27272A] px-3 py-2 label-mono hover:border-[#CFF048] hover:text-[#CFF048] flex items-center gap-2"
        >
          <ExternalLink size={14} /> VIEW WALL
        </Link>
        <button
          onClick={load}
          className="ml-auto border border-[#27272A] px-3 py-2 label-mono hover:border-[#CFF048] hover:text-[#CFF048] flex items-center gap-2"
          data-testid="community-refresh-btn"
        >
          <RefreshCcw size={14} /> REFRESH
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#27272A]">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 label-mono uppercase transition-colors -mb-px border-b-2 ${
              tab === t.id
                ? "text-[#CFF048] border-[#CFF048]"
                : "text-[#71717A] border-transparent hover:text-white"
            }`}
            data-testid={`community-tab-${t.id}`}
          >
            {t.label}
            {t.id === "pending" && counts.pending > 0 && (
              <span className="ml-2 bg-[#CFF048] text-black text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm">
                {counts.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center label-mono text-[#71717A] py-20">// LOADING...</div>
      ) : posts.length === 0 ? (
        <div className="border border-[#27272A] p-12 text-center label-mono text-[#71717A]" data-testid="community-empty">
          // NO {tab.toUpperCase()} POSTS
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="community-list">
          {posts.map((p) => {
            const status = p.status || "pending";
            const busy = actingId === p.id;
            return (
              <div
                key={p.id}
                className="border border-[#27272A] bg-[#0A0A0A] flex flex-col"
                data-testid={`admin-post-${p.id}`}
              >
                <div className="relative bg-black aspect-square">
                  <img
                    src={absoluteImg(p.image_url)}
                    alt={p.caption}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  {p.pinned && (
                    <span className="absolute top-2 left-2 bg-[#CFF048] text-black px-2 py-0.5 label-mono text-[10px] flex items-center gap-1">
                      <Pin size={10} /> PINNED
                    </span>
                  )}
                  <div className="absolute top-2 right-2">
                    <StatusPill status={status} />
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="font-display font-bold text-sm truncate">{p.user_name || "Anonymous"}</p>
                  <p className="label-mono text-[#71717A] text-[10px] truncate">{p.user_email}</p>
                  {p.caption && (
                    <p className="text-[#A1A1AA] text-xs mt-2 line-clamp-3 leading-relaxed">{p.caption}</p>
                  )}
                  {p.product_name && (
                    <p className="label-mono text-[#CFF048] mt-2 text-[10px] truncate">// {p.product_name}</p>
                  )}
                  <div className="flex items-center gap-3 mt-3 label-mono text-[#71717A]">
                    <span className="flex items-center gap-1"><Heart size={12} /> {p.like_count || 0}</span>
                    <span className="flex items-center gap-1"><MessageCircle size={12} /> {p.comment_count || 0}</span>
                    <span className="ml-auto text-[10px]">{new Date(p.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    {status !== "approved" && (
                      <button
                        onClick={() => act(p.id, "approve")}
                        disabled={busy}
                        className="bg-[#CFF048] text-black font-mono font-bold text-xs px-2 py-2 flex items-center justify-center gap-1 hover:bg-white transition-colors disabled:opacity-50"
                        data-testid={`approve-post-${p.id}`}
                      >
                        <Check size={12} /> APPROVE
                      </button>
                    )}
                    {status !== "rejected" && (
                      <button
                        onClick={() => act(p.id, "reject")}
                        disabled={busy}
                        className="border border-[#27272A] text-white font-mono font-bold text-xs px-2 py-2 flex items-center justify-center gap-1 hover:border-red-500 hover:text-red-400 disabled:opacity-50"
                        data-testid={`reject-post-${p.id}`}
                      >
                        <X size={12} /> REJECT
                      </button>
                    )}
                    {status === "approved" && (
                      <button
                        onClick={() => act(p.id, p.pinned ? "unpin" : "pin")}
                        disabled={busy}
                        className="border border-[#27272A] text-white font-mono font-bold text-xs px-2 py-2 flex items-center justify-center gap-1 hover:border-[#CFF048] hover:text-[#CFF048] disabled:opacity-50"
                        data-testid={`pin-post-${p.id}`}
                      >
                        {p.pinned ? <><PinOff size={12} /> UNPIN</> : <><Pin size={12} /> PIN</>}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm("Delete permanently?")) act(p.id, "delete");
                      }}
                      disabled={busy}
                      className="border border-[#27272A] text-[#71717A] font-mono font-bold text-xs px-2 py-2 flex items-center justify-center gap-1 hover:border-red-500 hover:text-red-400 disabled:opacity-50 col-span-2"
                      data-testid={`delete-post-${p.id}`}
                    >
                      <Trash2 size={12} /> DELETE
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
